<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">Tipos de documento</h1>
    </div>

    <div class="table-container">
      <div class="table-header">
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-start position-relative">
          <div class="w-full w-md-auto md-flex-1">
            <div class="d-flex justify-content-between align-items-center justify-content-lg-start">
              <button
                type="button"
                class="filter d-flex align-items-center justify-content-between border-0"
                @click="showFilters = !showFilters"
              >
                <i class="ri-filter-3-line text-dark font-weight-bold"></i>
                <div>Filtrar</div>
              </button>

              <div class="d-md-none">
                <button
                  type="button"
                  class="button-tooltip text-dark font-weight-bold m-0"
                  v-b-tooltip.hover
                  title="Recargar"
                  @click="loadData()"
                >
                  <i class="ri-loop-right-line"></i>
                </button>
              </div>
            </div>
          </div>

          <section class="w-full w-md-auto md-flex-1 d-flex justify-content-end align-items-center mt-2 mt-md-0">
            <div class="d-none d-md-flex">
              <button
                type="button"
                class="button-tooltip text-dark font-weight-bold"
                v-b-tooltip.hover
                title="Recargar"
                @click="loadData()"
              >
                <i class="ri-loop-right-line"></i>
              </button>
            </div>

            <div class="w-full w-md-auto d-flex flex-column flex-md-row">
              <SearchBar placeholder="Buscar por nombre" @search="onSearch" @input="search = $event" class="flex-1" />

              <div class="text-right mt-2 mt-md-0">
                <b-button variant="none" class="button button-primary ml-md-2" @click="openCreate">Agregar</b-button>
              </div>
            </div>
          </section>

          <filter-menu
            :show="showFilters"
            @hide="showFilters = false"
            :options="filterOptions"
            :selectedOptionFilter="filters"
            @setFilters="setFilters"
          ></filter-menu>
        </div>

        <div class="w-100 d-flex flex-wrap align-items-center mt-2" v-if="showFiltersList">
          <span v-for="(val, name) in filterList" v-bind:key="name" class="filter-item">
            {{ getFilterName(name, val) }}
            <button type="button" class="text-white p-0 bg-transparent border-0 ml-2" @click="deleteFilter(name)">
              <i class="ri-close-circle-line"></i>
            </button>
          </span>
        </div>
      </div>

      <div class="table-responsive mb-0 table-min-height">
        <table class="custom-table w-100">
          <thead>
            <tr>
              <th class="pl-4">Nombre</th>
              <th class="text-center">Aplica a</th>
              <th class="text-center">Vencimiento</th>
              <th class="text-center">Obligatorio</th>
              <th class="text-center">Alerta</th>
              <th class="text-center">Ciudad</th>
              <th class="text-center">Estado</th>
              <th class="pr-md-4">Opciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="o in list" :key="o.id">
              <td class="pl-4">{{ o.name }}</td>
              <td class="text-center">
                <span :class="`badge ${entityMeta(o.applies_to).color}`">{{ entityMeta(o.applies_to).label }}</span>
              </td>
              <td class="text-center">{{ o.has_expiration ? "Sí" : "No" }}</td>
              <td class="text-center">{{ o.is_required ? "Sí" : "No" }}</td>
              <td class="text-center">
                {{ o.alert_days_before != null ? o.alert_days_before + " días" : "-" }}
              </td>
              <td class="text-center">{{ o.city_name || "Todas" }}</td>
              <td class="text-center">
                <span :class="`badge ${getStatus(o.status).color}`">{{ getStatus(o.status).label }}</span>
              </td>
              <td class="text-center d_option pr-md-3">
                <DropdownTable>
                  <template slot="dropdown-content">
                    <DropdownItem text="Editar" @click="openEdit(o)" />
                    <DropdownItem :text="getStatus(o.status).action" @click="openChangeStatus(o)" />
                  </template>
                </DropdownTable>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="table-footer d-flex justify-content-end align-items-center" v-if="!list.length">
        <div class="table-no-results">
          <p class="m-0 px-3">No se encontraron resultados.</p>
        </div>
      </div>
    </div>

    <!-- Alta / edición -->
    <b-modal
      id="document-type-modal"
      :title="editing ? 'Editar tipo de documento' : 'Nuevo tipo de documento'"
      hide-footer
      size="md"
      @hidden="resetForm"
    >
      <form class="custom-form" @submit.prevent="save" novalidate>
        <div class="form-group">
          <label class="label">Nombre *</label>
          <input type="text" class="custom-form-control" v-model="form.name" maxlength="100" />
          <FormError :show="$v.form.name.$error" message="El nombre es requerido" />
        </div>

        <div class="form-group">
          <label class="label">Aplica a *</label>
          <FormSelect
            v-model="form.applies_to"
            :items="entityOptions"
            defaultOption="Elegir entidad"
            :disabled="editing"
            :showError="$v.form.applies_to.$error"
          />
          <FormError :show="$v.form.applies_to.$error" message="Selecciona a qué entidad aplica" />
          <div v-if="editing" class="text-muted small mt-1">
            No se puede cambiar: los documentos ya subidos quedarían huérfanos.
          </div>
        </div>

        <div class="row">
          <div class="col-md-6 form-group">
            <label class="label">¿Vence?</label>
            <div class="radio-group">
              <label class="radio-option mr-3">
                <input type="radio" :value="true" v-model="form.has_expiration" /> Sí
              </label>
              <label class="radio-option">
                <input type="radio" :value="false" v-model="form.has_expiration" /> No
              </label>
            </div>
          </div>
          <div class="col-md-6 form-group">
            <label class="label">¿Obligatorio?</label>
            <div class="radio-group">
              <label class="radio-option mr-3">
                <input type="radio" :value="true" v-model="form.is_required" /> Sí
              </label>
              <label class="radio-option">
                <input type="radio" :value="false" v-model="form.is_required" /> No
              </label>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6 form-group">
            <label class="label">Alertar días antes</label>
            <input
              type="number"
              class="custom-form-control"
              v-model.number="form.alert_days_before"
              min="0"
              max="365"
              :disabled="!form.has_expiration"
            />
          </div>
          <div class="col-md-6 form-group">
            <label class="label">Ciudad</label>
            <FormSelect v-model="form.city_id" :items="cityOptions" defaultOption="Todas las ciudades" />
          </div>
        </div>

        <div class="form-group">
          <label class="label">Descripción</label>
          <textarea class="custom-form-control" rows="2" v-model="form.description" maxlength="500"></textarea>
        </div>

        <div class="d-flex justify-content-end mt-3">
          <b-button variant="none" class="button button-light mr-2" @click="$bvModal.hide('document-type-modal')">
            Cancelar
          </b-button>
          <b-button variant="none" class="button button-primary" type="submit" :disabled="saving">
            {{ saving ? "Guardando..." : "Guardar" }}
          </b-button>
        </div>
      </form>
    </b-modal>

    <ConfirmationModal
      :show="confirmationModal.show"
      @hide="closeConfirmationModal"
      :data="confirmationModal"
      @confirm="confirmModal"
    />
  </div>
</template>

<script>
import {
  Alert,
  Constants,
  Util,
  FilterMixin,
  DropdownTable,
  DropdownItem,
  SearchBar,
  FilterMenu,
  FormSelect,
  FormError,
  ConfirmationModal,
  ConfirmationModalMixin,
} from "wize-admin";
import { required } from "vuelidate/lib/validators";

import { DocumentService, GeographyService } from "@/core/services";
import { Constants as LocalConstants } from "@/core/utils";

// Mantenedor de tipos de documento (DEF-ADM-019 §4.1). Un tipo aplica por igual
// a entidades propias y de proveedores.
export default {
  name: "DocumentTypeList",
  components: { SearchBar, FilterMenu, DropdownTable, DropdownItem, FormSelect, FormError, ConfirmationModal },
  mixins: [FilterMixin, ConfirmationModalMixin],
  data() {
    return {
      list: [],
      cities: [],
      editing: null,
      saving: false,
      form: {
        name: "",
        applies_to: null,
        has_expiration: true,
        is_required: false,
        alert_days_before: 30,
        description: "",
        city_id: null,
      },
      filterOptions: [
        {
          label: "Aplica a",
          key: "applies_to",
          type: Constants.TypeFilters.SINGLE,
          options: [
            LocalConstants.DocumentEntity.VEHICLE,
            LocalConstants.DocumentEntity.DRIVER,
            LocalConstants.DocumentEntity.PROVIDER,
          ],
        },
        {
          label: "Estado",
          key: "status",
          type: Constants.TypeFilters.SINGLE,
          options: [LocalConstants.DocumentTypeStatus.ACTIVE, LocalConstants.DocumentTypeStatus.INACTIVE],
        },
      ],
    };
  },
  validations: {
    form: {
      name: { required },
      applies_to: { required },
    },
  },
  computed: {
    entityOptions() {
      return Object.values(LocalConstants.DocumentEntity).map((e) => ({ key: e.key, label: e.label }));
    },
    cityOptions() {
      return this.cities.map((c) => ({ key: c.id, label: c.name }));
    },
  },
  methods: {
    entityMeta(key) {
      return Util.searchValue(key, LocalConstants.DocumentEntity) || { label: key, color: "badge-gray" };
    },
    getStatus(key) {
      return Util.searchValue(parseInt(key), LocalConstants.DocumentTypeStatus);
    },
    async loadData() {
      try {
        await this.$store.dispatch("app/loading", true);
        const res = await DocumentService.listTypes({ size: 200, search: this.search, ...this.filters });
        this.list = res.payload.items || [];
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "No se pudieron cargar los tipos de documento");
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    },
    async loadCities() {
      try {
        const res = await GeographyService.listCities();
        this.cities = res.payload.items || res.payload || [];
      } catch (ex) {
        console.error(ex);
        this.cities = [];
      }
    },
    openCreate() {
      this.editing = null;
      this.resetForm();
      this.$bvModal.show("document-type-modal");
    },
    openEdit(o) {
      this.editing = o;
      this.form = {
        name: o.name,
        applies_to: o.applies_to,
        has_expiration: o.has_expiration,
        is_required: o.is_required,
        alert_days_before: o.alert_days_before,
        description: o.description || "",
        city_id: o.city_id || null,
      };
      this.$bvModal.show("document-type-modal");
    },
    async save() {
      this.$v.$touch();
      if (this.$v.$invalid) return;

      this.saving = true;
      try {
        const data = {
          name: this.form.name,
          applies_to: this.form.applies_to,
          has_expiration: this.form.has_expiration,
          is_required: this.form.is_required,
          alert_days_before: this.form.has_expiration ? this.form.alert_days_before : null,
          description: this.form.description || null,
          city_id: this.form.city_id ? Number(this.form.city_id) : null,
        };
        if (this.editing) {
          await DocumentService.updateType(this.editing.id, data);
        } else {
          await DocumentService.saveType(data);
        }
        Alert.success("Tipo de documento guardado");
        this.$bvModal.hide("document-type-modal");
        await this.loadData();
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "No se pudo guardar el tipo de documento");
      } finally {
        this.saving = false;
      }
    },
    openChangeStatus(o) {
      this.selectedOption = o;
      this.openConfirmationModal({
        title: "Cambiar estado",
        description: "¿Está seguro que desea cambiar el estado?",
        acceptText: "Aceptar",
        action: "update",
      });
    },
    confirmModal() {
      this.closeConfirmationModal();
      if (this.confirmationModal.action === "update") this.changeStatus();
    },
    async changeStatus() {
      try {
        await this.$store.dispatch("app/loading", true);
        const status =
          parseInt(this.selectedOption.status) === LocalConstants.DocumentTypeStatus.ACTIVE.key
            ? LocalConstants.DocumentTypeStatus.INACTIVE.key
            : LocalConstants.DocumentTypeStatus.ACTIVE.key;
        await DocumentService.changeTypeStatus(this.selectedOption.id, status);
        await this.loadData();
        Alert.success("Estado actualizado correctamente");
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "No se pudo actualizar el estado");
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    },
    resetForm() {
      this.form = {
        name: "",
        applies_to: null,
        has_expiration: true,
        is_required: false,
        alert_days_before: 30,
        description: "",
        city_id: null,
      };
      this.editing = null;
      this.$v.$reset();
    },
  },
  async mounted() {
    await Promise.all([this.loadData(), this.loadCities()]);
  },
};
</script>

<style lang="stylus" scoped>
// Misma altura que un custom-form-control, para que la fila de radios quede
// alineada con los campos vecinos (igual que .type-radios en ProviderForm).
.radio-group
  display flex
  align-items center
  height 42px
.radio-option
  display flex
  align-items center
  gap 6px
  margin 0
  cursor pointer
  font-weight 500
</style>
